/**
 * 名前を正規化する
 * バックエンドの normalization.py と同等の処理
 *
 * 処理内容:
 * 1. Unicode正規化（NFKC形式）
 * 2. 法人格の統一
 * 3. 空白の正規化
 */
export const normalizeName = (name: string | null | undefined): string => {
  if (!name) return '';

  // 1. Unicode正規化（NFKC）
  // - 全角英数字・記号 → 半角
  // - 半角カタカナ → 全角
  // - 丸囲み数字などの互換文字の統一
  let normalized = name.normalize('NFKC');

  // 2. 法人格の統一
  // (株), （株）, ㈱ → 株式会社
  // (有), （有）, ㈲ → 有限会社
  normalized = normalized
    .replace(/\(株\)|（株）|㈱/g, '株式会社')
    .replace(/\(有\)|（有）|㈲/g, '有限会社')
    .replace(/\(同\)|（同）|㈴/g, '合同会社')
    .replace(/\(資\)|（資）|㈾/g, '合資会社')
    .replace(/\(名\)|（名）|㈲/g, '合名会社');

  // 3. 空白の正規化
  // - 前後の空白削除
  // - 連続する空白を単一の半角スペースに統一
  normalized = normalized.trim().replace(/\s+/g, ' ');

  return normalized;
};

/**
 * 住所を正規化する
 *
 * 処理内容:
 * 1. Unicode正規化（NFKC形式）
 * 2. 数字を半角に統一
 * 3. ハイフン・記号の統一
 * 4. 空白の削除
 * 5. 丁目・番地・号の表記統一
 */
export const normalizeAddress = (address: string | null | undefined): string => {
  if (!address) return '';

  // 1. Unicode正規化
  let normalized = address.normalize('NFKC');

  // 2. 数字を半角に統一（全角数字 → 半角）
  normalized = normalized.replace(/[０-９]/g, (s) =>
    String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
  );

  // 3. ハイフン・記号の統一
  normalized = normalized
    .replace(/[－−‐]/g, '-')  // 様々なハイフンを半角ハイフンに
    .replace(/[～〜]/g, '~');  // 波ダッシュの統一

  // 4. 空白の削除（住所の場合は空白を完全に削除）
  normalized = normalized.replace(/\s+/g, '');

  // 5. 丁目・番地・号の表記統一
  // 1丁目2番地3号 と 1-2-3 を統一
  normalized = normalized
    .replace(/丁目/g, '-')
    .replace(/番地/g, '-')
    .replace(/番/g, '-')
    .replace(/号/g, '');

  // 連続するハイフンを1つに
  normalized = normalized.replace(/-+/g, '-');

  return normalized;
};

/**
 * 郵便番号を正規化する
 *
 * 処理内容:
 * 1. ハイフン削除
 * 2. 数字のみ抽出
 * 3. 7桁に統一
 */
export const normalizePostalCode = (postalCode: string | null | undefined): string => {
  if (!postalCode) return '';

  // 数字のみ抽出
  const digitsOnly = postalCode.replace(/\D/g, '');

  // 7桁の場合はそのまま、それ以外は空文字
  return digitsOnly.length === 7 ? digitsOnly : '';
};

/**
 * 電話番号を正規化する
 *
 * 処理内容:
 * 1. ハイフン・括弧削除
 * 2. 数字のみ抽出
 * 3. 全角数字を半角に
 */
export const normalizePhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '';

  // Unicode正規化
  let normalized = phone.normalize('NFKC');

  // 全角数字を半角に
  normalized = normalized.replace(/[０-９]/g, (s) =>
    String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
  );

  // 数字のみ抽出
  const digitsOnly = normalized.replace(/\D/g, '');

  return digitsOnly;
};
